/**
 * @description
 * Development authentication provider that generates mock admin sessions
 * for local development. Mirrors production JWT structure exactly.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date January 9, 2025
 * @see /admin/src/lib/auth.ts
 */

import { SignJWT } from 'jose';
import type { User } from '../types/auth';
import { isDevelopment } from './environment';

/**
 * Development authentication configuration
 */
interface DevAuthConfig {
  jwtSecret: string;
  adminUser: User;
}

/**
 * Get development authentication configuration
 */
function getDevAuthConfig(): DevAuthConfig {
  const jwtSecret = import.meta.env.VITE_JWT_SECRET;
  
  if (!jwtSecret) {
    throw new Error('VITE_JWT_SECRET not found in development environment');
  }

  return {
    jwtSecret,
    adminUser: {
      id: '00000000-0000-0000-0000-000000000001',
      email: 'intars@byteable.ai',
      name: 'Intars (Dev)',
      provider: 'google',
      avatar: 'https://via.placeholder.com/150',
      createdAt: new Date().toISOString(),
      lastLoginAt: new Date().toISOString(),
    },
  };
}

/**
 * Generate a development JWT token
 * Uses the same structure and secret as production
 */
export async function generateDevToken(): Promise<string> {
  if (!isDevelopment()) {
    throw new Error('Dev authentication can only be used in development mode');
  }

  const config = getDevAuthConfig();
  const secret = new TextEncoder().encode(config.jwtSecret);

  const jwt = await new SignJWT({
    sub: config.adminUser.id,
    email: config.adminUser.email,
    name: config.adminUser.name,
    provider: config.adminUser.provider,
    avatar: config.adminUser.avatar,
    role: 'admin',
    dev: true, // Mark as dev token for identification
  })
    .setProtectedHeader({ alg: 'HS256' })
    .setIssuedAt()
    .setExpirationTime('7d')
    .sign(secret);

  console.log('🔓 DEV MODE: Generated mock JWT token');
  return jwt;
}

/**
 * Get development user information
 */
export function getDevUser(): User {
  if (!isDevelopment()) {
    throw new Error('Dev authentication can only be used in development mode');
  }

  const config = getDevAuthConfig();
  console.log('🔓 DEV MODE: Returning mock admin user');
  return config.adminUser;
}

/**
 * Store development session in localStorage
 * Mirrors production session storage
 */
export function storeDevSession(token: string): void {
  if (!isDevelopment()) {
    throw new Error('Dev authentication can only be used in development mode');
  }

  localStorage.setItem('auth_token', token);
  console.log('🔓 DEV MODE: Stored mock session in localStorage');
}

/**
 * Check if current token is a development token
 */
export function isDevToken(token: string): boolean {
  try {
    // Decode JWT payload without verification (just for checking dev flag)
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.dev === true;
  } catch {
    return false;
  }
}

/**
 * Initialize development authentication
 * Generates and stores a dev token automatically
 */
export async function initializeDevAuth(): Promise<User> {
  if (!isDevelopment()) {
    throw new Error('Dev authentication can only be used in development mode');
  }

  console.log('🔓 DEV MODE: Initializing development authentication');
  
  const token = await generateDevToken();
  storeDevSession(token);
  
  return getDevUser();
}

/**
 * Development authentication debug info
 */
export function getDevAuthDebugInfo() {
  if (!isDevelopment()) {
    return { error: 'Not in development mode' };
  }

  const config = getDevAuthConfig();
  const storedToken = localStorage.getItem('auth_token');
  
  return {
    isDev: true,
    hasStoredToken: !!storedToken,
    isDevToken: storedToken ? isDevToken(storedToken) : false,
    adminUser: config.adminUser,
    jwtSecretPresent: !!config.jwtSecret,
  };
}

/*
 * © 2025 Byte Inc. All rights reserved.
 * This code is the confidential and proprietary information of Byte Inc.
 * You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Byte Inc.
 */
